import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { ShoppingCart, ChevronDown, Menu, X } from 'lucide-react'
import Logo from './Logo'
import { groupByCluster, fmtPrice } from './marketingMeta'

// Headless nav chrome — the centre links + external button come from
// GET /api/v1/site/nav (admin/agent-editable). The default mirrors the server
// default so the nav renders correctly before the fetch resolves.
const DEFAULT_NAV = {
  links: [{ label: 'Use cases', href: '/use-cases' }, { label: 'Pricing', href: '/products' }, { label: 'User Guide', href: '/guide' }],
  external: { label: 'reright.io', href: 'https://reright.io' },
}
const navArr = (v) => (Array.isArray(v) ? v : [])
const isExternalHref = (href) => typeof href === 'string' && (/^(https?:)?\/\//.test(href) || href.startsWith('#'))

/**
 * Apple-style marketing nav for ai.reright.io — slim, sticky, frosted, calm.
 * Brand mark (canonical Logo) left; Products opens a FULL-WIDTH mega panel
 * grouped by capability lane (Answer / Build / Sell / Grow / Resell) with each
 * service deep-linked to its /products/<slug> page and its live price. Right
 * side: reright.io ↗, Pricing, User Guide, cart, Sign in. Hover + focus + Esc.
 *
 * Props:
 *  - services:  enriched catalog rows [{ key, name, price, icon }] (live price)
 *  - cartCount: total seats in cart (badge)
 *  - onOpenCart(): open the cart drawer
 *  - onHash(href): smooth-scroll handler for in-page (#...) links
 *  - signIn: { to, label, router } — router→<Link>, else <a>
 */
export default function MarketingNav({ services = [], cartCount = 0, onOpenCart, onHash, signIn }) {
  const [mega, setMega] = useState(false)     // Products mega panel open
  const [mobile, setMobile] = useState(false) // mobile sheet open
  const [nav, setNav] = useState(DEFAULT_NAV)  // headless nav chrome
  const wrapRef = useRef(null)
  const closeTimer = useRef(null)
  const groups = groupByCluster(services)
  // Signed-out visitors get Sign up + Forgot password; signed-in get "Open console".
  const loggedOut = !/console|dashboard/i.test(signIn?.label || '')

  useEffect(() => {
    let alive = true
    axios.get('/api/v1/site/nav')
      .then(({ data }) => {
        if (!alive) return
        const n = data && data.nav && typeof data.nav === 'object' ? data.nav : null
        if (n) setNav({
          links: navArr(n.links).length ? n.links : DEFAULT_NAV.links,
          external: n.external && typeof n.external === 'object' ? n.external : DEFAULT_NAV.external,
        })
      })
      .catch(() => { /* keep the built-in default */ })
    return () => { alive = false }
  }, [])

  // Close mega on Escape or on scroll (so the panel never lingers over the page).
  useEffect(() => {
    if (!mega) return
    const onKey = (e) => { if (e.key === 'Escape') setMega(false) }
    const onScroll = () => setMega(false)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => { document.removeEventListener('keydown', onKey); window.removeEventListener('scroll', onScroll) }
  }, [mega])

  // Lock body scroll while the mobile sheet is open.
  useEffect(() => {
    if (!mobile) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [mobile])

  const openMega = () => { clearTimeout(closeTimer.current); setMega(true) }
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setMega(false), 120) }
  // Close only when focus truly leaves the Products wrapper (keyboard a11y).
  const onWrapBlur = (e) => { if (!wrapRef.current?.contains(e.relatedTarget)) setMega(false) }

  const hashClick = (href) => (e) => { if (onHash && href?.startsWith('#')) { e.preventDefault(); onHash(href) } }

  const SignIn = ({ className }) => signIn?.router
    ? <Link to={signIn.to} className={className}>{signIn.label}</Link>
    : <a href={signIn.to} className={className} onClick={hashClick(signIn.to)}>{signIn.label}</a>

  // A single headless nav link — external/hash → <a>, internal path → SPA <Link>.
  const NavItem = ({ href, label, className, onClick }) => isExternalHref(href)
    ? <a href={href} className={className} onClick={(e) => { hashClick(href)(e); if (onClick) onClick() }}>{label}</a>
    : <Link to={href} className={className} onClick={onClick}>{label}</Link>

  const navLink = 'text-[13px] text-gray-600 hover:text-gray-900 transition-colors'

  return (
    <header className="sticky top-0 z-50">
      {/* bar */}
      <div className="mkt-frost relative border-b border-gray-200/80 bg-gray-50/80 backdrop-blur-xl backdrop-saturate-150">
        <div className="max-w-6xl mx-auto px-5 h-12 flex items-center justify-between">
          {/* brand */}
          <Link to="/home" className="flex items-center shrink-0" aria-label="ReRight home" onClick={() => setMega(false)}>
            <span className="scale-90 origin-left"><Logo size="sm" /></span>
          </Link>

          {/* center / left links (desktop) */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
            {/* Products — mega trigger */}
            <div
              ref={wrapRef}
              className="relative"
              onMouseEnter={openMega}
              onMouseLeave={scheduleClose}
              onFocus={openMega}
              onBlur={onWrapBlur}
            >
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={mega}
                onClick={() => setMega((v) => !v)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full ${navLink} ${mega ? 'text-gray-900 bg-gray-900/[0.04]' : ''}`}
              >
                Products
                <ChevronDown size={13} className={`transition-transform duration-200 ${mega ? 'rotate-180' : ''}`} />
              </button>

              {/* FULL-WIDTH mega panel */}
              <div
                className={`fixed left-0 right-0 top-12 z-50 origin-top transition-all duration-200 ${mega ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'}`}
              >
                <div className="border-b border-gray-200 bg-white shadow-[0_24px_48px_-16px_rgba(15,23,42,0.28)]">
                  <div className="max-w-6xl mx-auto px-5 py-8">
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-8">
                      {groups.map((g) => (
                        <div key={g.name}>
                          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary-600">{g.name}</p>
                          <p className="text-[11px] text-gray-400 mt-0.5 mb-3 leading-snug">{g.tagline}</p>
                          <ul className="space-y-0.5">
                            {g.items.map((s) => {
                              const Icon = s.icon
                              return (
                                <li key={s.key}>
                                  <a
                                    href={`/products/${s.slug}`}
                                    className="group flex items-center gap-2.5 -mx-2 px-2 py-1.5 rounded-lg hover:bg-gray-900/[0.04] transition-colors"
                                  >
                                    {Icon && (
                                      <span className="w-6 h-6 rounded-md bg-primary-500/10 flex items-center justify-center shrink-0">
                                        <Icon size={13} className="text-primary-600" />
                                      </span>
                                    )}
                                    <span className="min-w-0 flex-1">
                                      <span className="block text-[13px] font-medium text-gray-900 leading-tight truncate group-hover:text-primary-600 transition-colors">{s.name}</span>
                                    </span>
                                    <span className="text-[11px] text-gray-400 tabular-nums shrink-0">{fmtPrice(s.price)}</span>
                                  </a>
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="mt-7 pt-5 border-t border-gray-200 flex items-center justify-between flex-wrap gap-3">
                      <p className="text-[12px] text-gray-500">One login for every service. Pay only for what you turn on.</p>
                      <div className="flex items-center gap-5">
                        <Link to="/use-cases" onClick={() => setMega(false)} className="text-[13px] font-semibold text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
                          Browse by use case <span aria-hidden="true">→</span>
                        </Link>
                        <a href="/products" className="text-[13px] font-semibold text-primary-600 hover:underline inline-flex items-center gap-1">
                          See all products <span aria-hidden="true">→</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {navArr(nav.links).map((l, i) => (
              l && l.label ? <NavItem key={i} href={l.href || '#'} label={l.label} className={`px-3 py-1.5 rounded-full ${navLink}`} /> : null
            ))}
          </nav>

          {/* right cluster */}
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={nav.external?.href || 'https://reright.io'}
              className="hidden sm:inline-flex items-center gap-1 text-[12px] font-semibold text-primary-600 border border-primary-600/30 rounded-full px-3 py-1 hover:bg-primary-600 hover:text-white hover:border-primary-600 transition-colors whitespace-nowrap"
            >
              {nav.external?.label || 'reright.io'} <span className="text-[10px]" aria-hidden="true">↗</span>
            </a>

            <button onClick={onOpenCart} className="relative p-1.5 text-gray-600 hover:text-gray-900 transition-colors" aria-label={`Cart, ${cartCount} item${cartCount === 1 ? '' : 's'}`}>
              <ShoppingCart size={18} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-primary-600 text-white text-[10px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center tabular-nums">{cartCount}</span>
              )}
            </button>

            {/* Sign up — desktop secondary (mobile lives in the sheet) */}
            {loggedOut && (
              <Link to="/signup" className="hidden md:inline-flex items-center rounded-full border border-gray-300 text-gray-700 text-[13px] font-semibold px-3.5 py-1.5 hover:border-gray-400 hover:text-gray-900 transition-colors whitespace-nowrap">Sign up</Link>
            )}
            {/* Sign in — visible on ALL sizes (compact on mobile) so it's never hidden */}
            <SignIn className="inline-flex items-center rounded-full bg-primary-600 text-white text-[12px] sm:text-[13px] font-semibold px-3 sm:px-4 py-1.5 hover:bg-primary-700 transition-colors whitespace-nowrap" />

            {/* mobile hamburger */}
            <button onClick={() => setMobile(true)} className="md:hidden p-1.5 text-gray-700 hover:text-gray-900" aria-label="Open menu">
              <Menu size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* mobile sheet */}
      {mobile && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobile(false)} />
          <div className="absolute top-0 right-0 h-full w-[86%] max-w-sm bg-gray-50 border-l border-gray-200 shadow-2xl flex flex-col">
            <div className="h-12 px-5 flex items-center justify-between border-b border-gray-200">
              <span className="scale-[0.62] origin-left -mr-4"><Logo size="sm" /></span>
              <button onClick={() => setMobile(false)} className="p-1.5 text-gray-500 hover:text-gray-900" aria-label="Close menu"><X size={19} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {/* Auth — prominent at the TOP so it's the first thing visible on a phone */}
              {loggedOut ? (
                <div className="flex flex-col gap-2.5 mb-6">
                  <SignIn className="w-full inline-flex items-center justify-center rounded-full bg-primary-600 text-white text-sm font-semibold px-4 py-2.5 hover:bg-primary-700 transition-colors" />
                  <Link to="/signup" onClick={() => setMobile(false)} className="w-full inline-flex items-center justify-center rounded-full border border-gray-300 text-gray-800 text-sm font-semibold px-4 py-2.5">Create account</Link>
                  <Link to="/forgot-password" onClick={() => setMobile(false)} className="text-center text-[13px] text-gray-500 hover:text-gray-800 py-0.5">Forgot password?</Link>
                </div>
              ) : (
                <SignIn className="w-full inline-flex items-center justify-center rounded-full bg-primary-600 text-white text-sm font-semibold px-4 py-2.5 hover:bg-primary-700 transition-colors mb-6" />
              )}
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                <a href={nav.external?.href || 'https://reright.io'} className="flex-1 text-center text-[13px] font-semibold text-primary-600 border border-primary-600/30 rounded-full px-3 py-2 whitespace-nowrap">{nav.external?.label || 'reright.io'} ↗</a>
                {navArr(nav.links).map((l, i) => (
                  l && l.label ? <NavItem key={i} href={l.href || '#'} label={l.label} onClick={() => setMobile(false)} className="flex-1 text-center text-[13px] font-medium text-gray-700 border border-gray-200 rounded-full px-3 py-2 whitespace-nowrap" /> : null
                ))}
              </div>
              <Link to="/use-cases" onClick={() => setMobile(false)} className="block text-center text-[13px] font-semibold text-primary-600 border border-primary-600/25 rounded-full px-3 py-2 mb-5">Browse by use case →</Link>
              {groups.map((g) => (
                <div key={g.name} className="mb-5">
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary-600 mb-2">{g.name}</p>
                  <ul className="space-y-0.5">
                    {g.items.map((s) => {
                      const Icon = s.icon
                      return (
                        <li key={s.key}>
                          <a href={`/products/${s.slug}`} className="flex items-center gap-2.5 -mx-2 px-2 py-2 rounded-lg hover:bg-gray-900/[0.04]">
                            {Icon && <span className="w-7 h-7 rounded-md bg-primary-500/10 flex items-center justify-center shrink-0"><Icon size={14} className="text-primary-600" /></span>}
                            <span className="flex-1 text-[14px] font-medium text-gray-900">{s.name}</span>
                            <span className="text-[12px] text-gray-400 tabular-nums">{fmtPrice(s.price)}</span>
                          </a>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
              <a href="/products" className="block text-center text-[13px] font-semibold text-primary-600 py-2">See all products →</a>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
